/**
 * レポートMarkdownパーサー
 *
 * Markdown本文内の {{type:id}} 記法をパースし、
 * テキスト部分と要素参照に分離する
 */

/**
 * パース結果のトークン型
 */
export type ReportToken =
  | { type: 'text'; content: string }
  | { type: 'element'; elementType: string; elementId: string };

/**
 * 要素参照のパターン: {{type:id}}
 * - type: graph_builder, crosstab, statistics_summary, data_table, qq_plot, cross_dataset_comparison
 * - id: 要素ID
 */
const ELEMENT_REFERENCE_PATTERN = /\{\{([a-z_]+):([^}]+)\}\}/g;

/**
 * Markdown本文をトークンに分割する
 *
 * @param content - Markdown本文
 * @returns トークンの配列
 *
 * @example
 * ```typescript
 * const tokens = parseReportContent('Hello\n\n{{graph_builder:graph-123}}\n\nWorld');
 * // [
 * //   { type: 'text', content: 'Hello\n\n' },
 * //   { type: 'element', elementType: 'graph_builder', elementId: 'graph-123' },
 * //   { type: 'text', content: '\n\nWorld' }
 * // ]
 * ```
 */
export function parseReportContent(content: string): ReportToken[] {
  const tokens: ReportToken[] = [];
  let lastIndex = 0;

  // 正規表現をリセット
  ELEMENT_REFERENCE_PATTERN.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = ELEMENT_REFERENCE_PATTERN.exec(content)) !== null) {
    // マッチ前のテキスト部分
    if (match.index > lastIndex) {
      const textContent = content.slice(lastIndex, match.index);
      if (textContent) {
        tokens.push({ type: 'text', content: textContent });
      }
    }

    // 要素参照
    tokens.push({
      type: 'element',
      elementType: match[1],
      elementId: match[2]
    });

    lastIndex = match.index + match[0].length;
  }

  // 残りのテキスト部分
  if (lastIndex < content.length) {
    const remainingText = content.slice(lastIndex);
    if (remainingText) {
      tokens.push({ type: 'text', content: remainingText });
    }
  }

  return tokens;
}

/**
 * 要素参照を生成する
 *
 * @param elementType - 要素タイプ
 * @param elementId - 要素ID
 * @returns 要素参照文字列 (例: "{{graph_builder:graph-123}}")
 */
export function createElementReference(elementType: string, elementId: string): string {
  return `{{${elementType}:${elementId}}}`;
}

/**
 * Markdown本文から指定した要素の参照を削除する
 *
 * @param content - Markdown本文
 * @param elementId - 削除する要素のID
 * @returns 更新されたMarkdown本文
 */
export function removeElementReference(content: string, elementId: string): string {
  // 要素参照とその前後の空行を削除
  // パターン: 改行 + {{type:id}} + 改行 を空行に
  const pattern = new RegExp(`\\n*\\{\\{[a-z_]+:${escapeRegExp(elementId)}\\}\\}\\n*`, 'g');
  return content.replace(pattern, '\n\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Markdown本文の末尾に要素参照を追加する
 *
 * @param content - Markdown本文
 * @param elementType - 要素タイプ
 * @param elementId - 要素ID
 * @returns 更新されたMarkdown本文
 */
export function appendElementReference(
  content: string,
  elementType: string,
  elementId: string
): string {
  const reference = createElementReference(elementType, elementId);
  const trimmedContent = content.trimEnd();

  if (trimmedContent === '') {
    return reference;
  }

  return `${trimmedContent}\n\n${reference}`;
}

/**
 * Markdown本文内の要素参照IDを抽出する
 *
 * @param content - Markdown本文
 * @returns 参照されている要素IDの配列
 */
export function extractElementIds(content: string): string[] {
  const ids: string[] = [];
  ELEMENT_REFERENCE_PATTERN.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = ELEMENT_REFERENCE_PATTERN.exec(content)) !== null) {
    ids.push(match[2]);
  }

  return ids;
}

/**
 * 正規表現の特殊文字をエスケープする
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
